import React from 'react';
import { CaretDown, X } from '@phosphor-icons/react';
import { useImmoTheme } from '../../../../../lib/immobilien/theme';

export type FilterGelesen = 'alle' | 'gelesen' | 'ungelesen';
export type FilterZuordnung = 'alle' | 'mit' | 'ohne';
export type FilterErledigt = 'alle' | 'offen' | 'erledigt';
export type FilterTriage = 'alle' | 'ohne_ticket' | 'notfall';

export interface FilterWerte {
  gelesen: FilterGelesen;
  zuordnung: FilterZuordnung;
  erledigt: FilterErledigt;
  zustaendig: string;
  markiert: boolean;
  anhang: boolean;
  triage: FilterTriage;
}

export interface FilterPanelProps {
  werte: FilterWerte;
  setGelesen: (v: FilterGelesen) => void;
  setZuordnung: (v: FilterZuordnung) => void;
  setErledigt: (v: FilterErledigt) => void;
  setZustaendig: (v: string) => void;
  setMarkiert: (fn: (v: boolean) => boolean) => void;
  setAnhang: (fn: (v: boolean) => boolean) => void;
  setTriage: (v: FilterTriage) => void;
  teams: { id: string; name: string }[];
  teamMembers: { id: string; full_name: string }[];
  /** Triage-Knöpfe nur im Posteingang. */
  mitTriage: boolean;
  ohneTicketAnzahl: number;
  notfallAnzahl: number;
  anzahl: number;
  hatFilter: boolean;
  onReset: () => void;
  /** Mobil 44 px. */
  hoehe?: number;
}

const LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)', padding: '0 2px 6px' };

function Wahl({ label, wert, onChange, hoehe, children }: { label: string; wert: string; onChange: (v: string) => void; hoehe: number; children: React.ReactNode }) {
  // Das Popover hängt an document.body (außerhalb .immo-flaeche) – Optionsliste trotzdem im passenden Theme.
  const [theme] = useImmoTheme();
  return (
    <label className="flex flex-col min-w-0">
      <span style={LABEL}>{label}</span>
      <span className="relative flex">
        <select value={wert} onChange={e => onChange(e.target.value)} className="w-full min-w-0 cursor-pointer"
          style={{ appearance: 'none', WebkitAppearance: 'none', height: hoehe, padding: '0 34px 0 12px', borderRadius: 13, border: 'none', outline: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', colorScheme: theme }}>
          {children}
        </select>
        <CaretDown size={14} weight="bold" color="var(--ink3)" className="absolute pointer-events-none" style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }} />
      </span>
    </label>
  );
}

function Schalter({ an, onClick, hoehe, gefahr = false, children }: { an: boolean; onClick: () => void; hoehe: number; gefahr?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" aria-pressed={an} onClick={onClick} className="flex items-center justify-center cursor-pointer min-w-0"
      style={{
        height: hoehe, padding: '0 12px', gap: 8, borderRadius: 13, border: 'none', fontSize: 13, fontWeight: 700,
        background: an ? (gefahr ? 'var(--bad-soft)' : 'var(--acc-soft)') : 'var(--fill2)',
        color: an ? (gefahr ? 'var(--bad)' : 'var(--acc)') : 'var(--ink)',
        boxShadow: an ? `inset 0 0 0 2px ${gefahr ? 'var(--bad)' : 'var(--acc)'}` : 'none',
      }}>
      {children}
    </button>
  );
}

/** Filter der Liste (Screen 03). Optionen und Texte wie bisher in der Insel-Pille. */
export function FilterPanel(p: FilterPanelProps) {
  const h = p.hoehe ?? 40;
  const o = (v: string, l: string) => <option value={v}>{l}</option>;
  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
        <Wahl label="Status" wert={p.werte.gelesen} onChange={v => p.setGelesen(v as FilterGelesen)} hoehe={h}>
          {o('alle', 'Alle')}{o('ungelesen', 'Ungelesen')}{o('gelesen', 'Gelesen')}
        </Wahl>
        <Wahl label="Zuordnung" wert={p.werte.zuordnung} onChange={v => p.setZuordnung(v as FilterZuordnung)} hoehe={h}>
          {o('alle', 'Alle')}{o('mit', 'Mit')}{o('ohne', 'Ohne')}
        </Wahl>
        <Wahl label="Erledigt" wert={p.werte.erledigt} onChange={v => p.setErledigt(v as FilterErledigt)} hoehe={h}>
          {o('alle', 'Alle')}{o('offen', 'Offen')}{o('erledigt', 'Erledigt')}
        </Wahl>
        <Wahl label="Zuständig" wert={p.werte.zustaendig} onChange={p.setZustaendig} hoehe={h}>
          {o('alle', 'Alle')}{o('global', 'Global')}
          <optgroup label="Mitarbeiter">{p.teamMembers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}</optgroup>
          <optgroup label="Teams">{p.teams.map(t => <option key={t.id} value={'t:' + t.id}>{t.name}</option>)}</optgroup>
        </Wahl>
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
        <Schalter an={p.werte.markiert} onClick={() => p.setMarkiert(v => !v)} hoehe={h}>Nur markiert</Schalter>
        <Schalter an={p.werte.anhang} onClick={() => p.setAnhang(v => !v)} hoehe={h}>Nur mit Anhang</Schalter>
        {p.mitTriage && (
          <>
            <Schalter an={p.werte.triage === 'ohne_ticket'} onClick={() => p.setTriage(p.werte.triage === 'ohne_ticket' ? 'alle' : 'ohne_ticket')} hoehe={h}>
              <span className="flex-1 text-left truncate">Ohne Ticket</span><span className="immo-zahl">{p.ohneTicketAnzahl}</span>
            </Schalter>
            <Schalter gefahr an={p.werte.triage === 'notfall'} onClick={() => p.setTriage(p.werte.triage === 'notfall' ? 'alle' : 'notfall')} hoehe={h}>
              <span className="flex-1 text-left truncate">Nur Notfall</span><span className="immo-zahl">{p.notfallAnzahl}</span>
            </Schalter>
          </>
        )}
      </div>
      <div className="flex items-center justify-between" style={{ borderTop: '1px solid var(--line)', paddingTop: 10, minHeight: 32 }}>
        <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{p.anzahl} {p.anzahl === 1 ? 'E-Mail' : 'E-Mails'}</span>
        {p.hatFilter && (
          <button type="button" onClick={p.onReset} className="flex items-center cursor-pointer"
            style={{ height: h === 44 ? 44 : 30, padding: '0 12px', gap: 5, borderRadius: 15, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, fontWeight: 800 }}>
            <X size={11} weight="bold" />Reset
          </button>
        )}
      </div>
    </div>
  );
}
